/**
 * Meeple geometry: small extruded figures in the six seat colours. [P5-board]
 *
 * No external model — a Carcassonne meeple's silhouette (round head, splayed body) is simple enough to
 * author as a 2D `Shape` and extrude, which keeps this consistent with the "no external art assets"
 * rule ([D-06]) that governs the tile textures too.
 */

import {
  BackSide,
  DoubleSide,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  Shape,
  Vector3,
} from 'three';

const MEEPLE_HEIGHT = 0.16;
const MEEPLE_SCALE = 0.21;

/**
 * The dark rim. [P6-contrast]
 *
 * Tony lost a red meeple on tile `X` — the crossroads, four roads meeting at stops — because it sat
 * among the red-roofed houses of his own artwork and simply vanished. That is a hue collision, not a
 * bad anchor: the anchor is correct and there is nowhere on that tile a red piece would reliably read.
 * Moving anchors per-colour would be a rule with six exceptions; redrawing the art is off the table.
 *
 * So the piece carries its own separation. A second copy of the same geometry, grown slightly and
 * rendered `BackSide`, is hidden behind the meeple everywhere except at its silhouette, where it shows
 * as a thin dark outline. It costs one draw call, changes no coordinates, and works on every tile and
 * every seat colour — including a dark meeple on a dark field, which had the same problem in reverse.
 */
const OUTLINE_GROWTH = 1.16;
const OUTLINE_COLOUR = '#14100c';

/**
 * Body: a little pawn/person silhouette — legs, splayed arms, a neck — traced in local units where
 * 1 unit ≈ the meeple's total height before scaling. [P9-art] rounds off the three sharpest corners
 * (each heel, hand and shoulder) with a short `quadraticCurveTo` cut so the silhouette reads as the
 * classic rounded-off meeple rather than a sharp paper cutout; the coordinates are still the original
 * hand-authored vertices, just approached and left via a point partway down each adjacent edge
 * instead of meeting there in a point.
 */
function meepleShape(): Shape {
  const s = new Shape();
  s.moveTo(-0.16, 0);
  s.lineTo(-0.265, 0.015);
  s.quadraticCurveTo(-0.3, 0.02, -0.288, 0.068);
  s.lineTo(-0.22, 0.34);
  s.lineTo(-0.3604, 0.4648);
  s.quadraticCurveTo(-0.4, 0.5, -0.3824, 0.5352);
  s.lineTo(-0.32, 0.66);
  s.lineTo(-0.1796, 0.5508);
  s.quadraticCurveTo(-0.14, 0.52, -0.1312, 0.5376);
  s.lineTo(-0.1, 0.6);
  s.absarc(0, 0.74, 0.15, Math.PI * 1.05, Math.PI * -0.05, false);
  s.lineTo(0.1, 0.6);
  s.lineTo(0.1312, 0.5376);
  s.quadraticCurveTo(0.14, 0.52, 0.1796, 0.5508);
  s.lineTo(0.32, 0.66);
  s.lineTo(0.3824, 0.5352);
  s.quadraticCurveTo(0.4, 0.5, 0.3604, 0.4648);
  s.lineTo(0.22, 0.34);
  s.lineTo(0.288, 0.068);
  s.quadraticCurveTo(0.3, 0.02, 0.265, 0.015);
  s.lineTo(0.16, 0);
  s.closePath();
  return s;
}

/**
 * Two orientations, because the game distinguishes them.
 *
 * A farmer is laid on his BACK in the field — it is how you tell, across the table, that a meeple is
 * claiming a farm rather than the road or city on the same tile, and it matters right up to the end
 * because farms score last. Everyone else stands. We were laying every meeple down, which said
 * "farmer" about all of them.
 *
 * The extruded silhouette is upright in its natural XY orientation; laying it down is one quarter turn
 * about X, which drops the figure onto its back with its thickness becoming height.
 */
const sharedGeometry: { standing: ExtrudeGeometry | null; lying: ExtrudeGeometry | null } = {
  standing: null,
  lying: null,
};

function geometry(lying: boolean): ExtrudeGeometry {
  const cached = lying ? sharedGeometry.lying : sharedGeometry.standing;
  if (cached) return cached;
  const built = new ExtrudeGeometry(meepleShape(), {
    depth: MEEPLE_HEIGHT,
    bevelEnabled: true,
    bevelThickness: 0.024,
    bevelSize: 0.024,
    bevelSegments: 4,
    curveSegments: 12,
  });
  if (lying) {
    // On his back: the silhouette goes flat to the board and the extrusion depth becomes height.
    built.rotateX(-Math.PI / 2);
    built.translate(0, MEEPLE_HEIGHT, 0);
    sharedGeometry.lying = built;
  } else {
    // Standing: the silhouette stays vertical, feet already at y = 0. Centre the thickness so he is
    // not offset to one side of the point he is standing on.
    built.translate(0, 0, -MEEPLE_HEIGHT / 2);
    sharedGeometry.standing = built;
  }
  return built;
}

export function createMeepleMesh(colourHex: string, lying = false): Group {
  const group = new Group();
  // A slightly waxy/matte finish — mostly matte (fairly high roughness) with a faint clearcoat sheen,
  // like painted wood or wax rather than flat plastic. `MeshPhysicalMaterial` is already part of the
  // `three` core (extends `MeshStandardMaterial`), so this adds no new dependency.
  const material = new MeshPhysicalMaterial({
    color: colourHex,
    roughness: 0.68,
    metalness: 0,
    clearcoat: 0.18,
    clearcoatRoughness: 0.35,
    side: DoubleSide,
  });
  const geo = geometry(lying);
  const mesh = new Mesh(geo, material);
  mesh.castShadow = true;
  mesh.receiveShadow = false;
  mesh.scale.setScalar(MEEPLE_SCALE);
  group.add(mesh);
  group.add(outlineShell(geo));
  return group;
}

/**
 * Grow the silhouette about its own centre, not about the origin.
 *
 * Scaling about the origin would put no rim at all under a standing meeple's feet (they sit exactly at
 * y = 0, the fixed point) and a fat one over his head. Growing about the geometry's bounding-box centre
 * gives an even rim all the way round, which is the entire point of drawing it.
 */
function outlineShell(geo: ExtrudeGeometry): Mesh {
  geo.computeBoundingBox();
  const centre = geo.boundingBox?.getCenter(new Vector3()) ?? new Vector3();
  const shell = new Mesh(
    geo,
    // Basic, not physical: an outline must not pick up the scene lighting, or it stops being a constant
    // separator and becomes just another shaded surface competing with the tile beneath it.
    new MeshBasicMaterial({ color: OUTLINE_COLOUR, side: BackSide }),
  );
  shell.scale.setScalar(MEEPLE_SCALE * OUTLINE_GROWTH);
  shell.position.copy(centre).multiplyScalar(MEEPLE_SCALE * (1 - OUTLINE_GROWTH));
  shell.castShadow = false;
  shell.receiveShadow = false;
  shell.renderOrder = -1;
  return shell;
}
